import { Command } from 'ckeditor5/src/core';
import type { Editor } from 'ckeditor5/src/core';

export class NoteCommand extends Command {
	/**
	 * @inheritDoc
	 */
	constructor(editor: Editor) {
		super(editor);

		// Log command creation for debugging
		console.log('NoteCommand created');
	}

	/**
	 * @inheritDoc
	 */
	public override refresh(): void {
		// This command is always enabled
		this.isEnabled = true;
	}

	/**
	 * Executes the command.
	 *
	 * @param options.type The type of the note.
	 */
	public override execute(options: { type?: string } = {}): void {
		console.log('NoteCommand executing with options:', options);

		const model = this.editor.model;
		const selection = model.document.selection;
		const blocks = Array.from(selection.getSelectedBlocks());

		model.change((writer) => {
			// If a note is already selected, just change its type
			const existingNote = this._findNote(selection);

			if (existingNote) {
				// If the same type is selected, remove the note
				if (existingNote.getAttribute('noteType') === options.type) {
					this._removeNote(existingNote);
				} else {
					// Change the type of the note
					writer.setAttribute('noteType', options.type, existingNote);
				}
				return;
			}

			// Create a new note - use info as default type if none provided
			const newNote = writer.createElement('note', {
				noteType: options.type || 'info',
			});

			const noteContent = writer.createElement('noteContent');

			// Insert the new structure
			model.insertContent(newNote);
			writer.append(noteContent, newNote);

			// If there is content selected, move it into the noteContent
			if (blocks.length > 0 && !blocks[0].isEmpty) {
				// First place cursor inside noteContent
				writer.setSelection(noteContent, 'in');

				// Use the current selection instead of creating a range
				// This avoids the Range vs Selection type mismatch
				const selectedContent = model.getSelectedContent(selection);

				// Insert the selected content into the noteContent
				writer.insert(selectedContent, noteContent);
			} else {
				// No content selected, just put the selection inside the note
				writer.setSelection(noteContent, 'in');
			}
		});
	}

	/**
	 * Finds a note in the current selection.
	 */
	private _findNote(selection: any): any {
		const model = this.editor.model;
		const position = selection.getFirstPosition();

		if (!position) {
			return null;
		}

		let parent = position.parent;

		// Check if we're inside a noteContent
		if (parent.name === 'noteContent') {
			parent = parent.parent;
		}

		// Check if we found a note
		if (parent.name === 'note') {
			return parent;
		}

		return null;
	}

	/**
	 * Removes a note.
	 */
	private _removeNote(note: any): void {
		const model = this.editor.model;
		const noteContent = note.getChild(0);

		if (!noteContent || noteContent.isEmpty) {
			// If empty, just remove the entire structure
			model.deleteContent(model.createSelection(note, 'on'));
		} else {
			// Otherwise, extract the content and remove the structure
			model.change((writer) => {
				const range = writer.createRangeIn(noteContent);

				// Create a proper selection from the range
				const rangeSelection = writer.createSelection(range);
				const fragments = model.getSelectedContent(rangeSelection);

				// Remove the note
				model.deleteContent(model.createSelection(note, 'on'));

				// Insert the content that was inside
				model.insertContent(fragments);
			});
		}
	}
}
